<template>
	<div
		class="scroll-container"
		:style="{
			height: fixedHeight
				? fixedHeight
				: takeAvailableHeight
				? `${height}px`
				: 'auto'
		}"
		ref="scrollContainerRef"
	>
		<div
			v-if="overlay"
			class="scroll-container__overlay"
			:style="{
				height: fixedHeight
					? fixedHeight
					: takeAvailableHeight
					? `${height}px`
					: 'auto'
			}"
		/>
		<slot />
		<Spinner
			v-if="isInfinityScroll && hasMoreItems"
			:processor="{ loading: true }"
			variant="infinity-scroll"
		/>
		<div
			v-if="isInfinityScroll && !hasMoreItems && showEndResultByDefault"
			class="end-results"
		>
			End of results
		</div>
	</div>
</template>

<script>
import Spinner from '@/components/visual/spinner/spinner';

// libraries
import _ from 'lodash';

export default {
	name: 'ScrollContainer',
	components: {
		Spinner
	},
	props: {
		overlay: Boolean,
		/* this array is used to calculate all visible elements on the page and to adjust the height.
		 */
		variant: {
			type: String,
			default: '',
			validator: val => ['', 'infinity-scroll'].includes(val)
		},
		// required if variant == 'infinity-scroll'
		hasMoreItems: {
			type: Boolean,
			default: true
		},
		selectorArray: {
			type: Array,
			// common selections that are used on almost all pages
			default: () => [
				'nav.action-set-nav',
				'header.site__header',
				'.content-page__heading',
				'.results-toolbar',
				'footer.site__footer'
			]
		},
		extraSpace: {
			type: Number,
			default: 0
		},
		takeAvailableHeight: {
			type: Boolean,
			default: true
		},
		showEndResultByDefault: {
			type: Boolean,
			default: true
		},
		takeFixedHeight: {
			type: Object,
			default: () => {
				return {
					height: 0,
					unit: ''
				};
			}
		}
	},
	data() {
		return {
			height: '0px',
			gap: 2,
			resizeEvent: null,
			scrollEvent: null
		};
	},
	mounted() {
		this.calculateHeight();
		this.resizeEvent = window.addEventListener(
			'resize',
			this.calculateHeight
		);

		this.$nextTick(() => {
			this.calculateHeight();
		});

		if (!this.isInfinityScroll) {
			return;
		}

		this.checkEndOfScroll();

		this.scrollEvent = this.$refs.scrollContainerRef.addEventListener(
			'scroll',
			this.checkEndOfScroll
		);

		// TODO : emit scroll when there is available area and more items (hasMoreItems)
	},
	updated() {
		this.$nextTick(() => {
			this.calculateHeight();
		});
	},
	computed: {
		isInfinityScroll() {
			return this.variant === 'infinity-scroll';
		},
		fixedHeight() {
			const { height, unit } = this.takeFixedHeight;
			if (height && unit) {
				return `${height}${unit}`;
			}
			return '';
		}
	},
	methods: {
		calculateHeight() {
			if (this.selectorArray.length) {
				let selectorArrayHeight = 0;

				const pageHeight = window.innerHeight;

				this.selectorArray.forEach(element => {
					const newElement = document.querySelector(element);

					if (newElement) {
						const height = newElement.offsetHeight;
						const marginTop = parseInt(
							window.getComputedStyle(newElement).marginTop
						);
						const marginBottom = parseInt(
							window.getComputedStyle(newElement).marginBottom
						);
						const elTotalHeight = height + marginTop + marginBottom;

						selectorArrayHeight += elTotalHeight;
					}
				});

				this.height =
					pageHeight - (selectorArrayHeight + this.extraSpace + 16);
			}
		},
		checkEndOfScroll() {
			if (!this.hasScroll()) {
				return;
			}
			if (
				this.$refs.scrollContainerRef.scrollTop +
					this.gap +
					this.$refs.scrollContainerRef.clientHeight >=
				this.$refs.scrollContainerRef.scrollHeight
			) {
				_.debounce(() => this.$emit('scrollEnd'), 300)();
			}
		},
		hasScroll() {
			return (
				this.$refs.scrollContainerRef.scrollHeight >
				this.$refs.scrollContainerRef.clientHeight
			);
		}
	},
	beforeDestroy() {
		removeEventListener('resize', this.resizeEvent);
		removeEventListener('scroll', this.scrollEvent);
	}
};
</script>

<style lang="scss" scoped>
.scroll-container {
	overflow-y: auto;
	@include scrollbar;
	position: relative;

	&__overlay {
		position: fixed;
		width: 100%;
		max-width: 1200px;
		background: $backdrop-bkgd;
		pointer-events: auto;
		z-index: 101;
		-webkit-transition: opacity 100ms ease-in;
		-moz-transition: opacity 100ms ease-in;
		transition: opacity 100ms ease-in;
	}
}
</style>
